import FW from '../../fw/fw';
import Logger from '../../fw/logger';
import StreamingHelpers from '../streaming-helpers';
import ConfigureHlsJS from './config';
import CCManagement from '../../accessibility/cc';
import UIManagement from '../../ui/ui';
import DvrManagement from '../dvr';
import AdsImaDai from '../../ads/ima-dai';


export default class HlsJS {

  #rmp;
  #hlsJSConfig = {};
  #rawProgramDateTime = -1;
  #hlsManifestData = {};
  #hlsSessionData = [];
  #hlsRawID3KLVSamplesData = [];
  #currentLevelResetTried = false;
  #hlsLevelData = {};
  #hlsFragmentData = {};
  #hlsFragmentBeingPlayed = {};
  #hlsJSValidatedConfig = {};
  #hlsJSTextTracksCheckingIntervalFn = null;
  #hlsManifestParsedFn = null;
  #hlsJSAttachMultiAudioFn = null;
  #onPlayingCurrentLevelResetTriedFn = null;
  #hlsJSAbrIntervalFn = null;
  #hlsJSFramerateIntervalFn = null;
  #hlsJSLevelDuration = -1;
  #fragParsedNBFragment = 0;
  #onFragParsedFn = null;

  constructor(rmp) {
    this.#rmp = rmp;
    this.#rmp.rmpStreamingHelpers = new StreamingHelpers(rmp);
  }

  get hlsJSLevelDuration() {
    return this.#hlsJSLevelDuration;
  }

  get hlsManifestData() {
    return this.#hlsManifestData;
  }

  get hlsSessionData() {
    return this.#hlsSessionData;
  }

  get hlsLevelData() {
    return this.#hlsLevelData;
  }

  get hlsFragmentData() {
    return this.#hlsFragmentData;
  }

  get hlsFragmentBeingPlayedData() {
    return this.#hlsFragmentBeingPlayed;
  }

  get hlsRawID3KLVSamplesData() {
    return this.#hlsRawID3KLVSamplesData;
  }

  get rawProgramDateTime() {
    return this.#rawProgramDateTime;
  }

  #resetHlsJSVars() {
    // when we call setSrc before ready this.#hlsJSConfig.reset may not exist
    if (this.#hlsJSConfig.reset) {
      this.#hlsJSConfig.reset();
    }
    this.#rawProgramDateTime = -1;
    this.#hlsManifestData = {};
    this.#hlsSessionData = [];
    this.#currentLevelResetTried = false;
    this.#hlsLevelData = {};
    this.#hlsFragmentData = {};
    this.#hlsFragmentBeingPlayed = {};
    this.#hlsJSValidatedConfig = {};
    this.#hlsJSTextTracksCheckingIntervalFn = null;
    this.#hlsManifestParsedFn = null;
    this.#hlsJSAttachMultiAudioFn = null;
    this.#onPlayingCurrentLevelResetTriedFn = null;
    this.#hlsJSAbrIntervalFn = null;
    this.#hlsJSFramerateIntervalFn = null;
    this.#hlsJSLevelDuration = -1;
    this.#fragParsedNBFragment = 0;
    this.#onFragParsedFn = null;
  }

  #hlsJSTextTracksCheckingInterval() {
    if (this.#rmp.dom.video.textTracks.length > 0) {
      Logger.print('captions', 'following video.textTracks are now available', this.#rmp.dom.video.textTracks);
      window.clearInterval(this.#hlsJSTextTracksCheckingIntervalFn);
      this.#rmp.rmpCCManagement = new CCManagement(this.#rmp);
      this.#rmp.rmpCCManagement.readingCaptionsFromHls = true;
      this.#rmp.textTracks = StreamingHelpers.filterNativeTextTracks(this.#rmp.dom.video.textTracks);
      Logger.print('captions', 'filtered textTracks follows', this.#rmp.textTracks);
      if (this.#rmp.textTracks.length > 0) {
        this.#rmp.textTracks.forEach((textTrack, index) => {
          this.#rmp.loadedCCFiles[index] = {};
          let lng = textTrack.language || textTrack.lang;
          if (!lng) {
            lng = this.#rmp.labels.hint.track + ' ' + (index + 1);
          }
          this.#rmp.loadedCCFiles[index].lng = lng;
          let label = textTrack.label || textTrack.name;
          if (!label) {
            label = FW.mapLanguageCodeToName(textTrack.language || textTrack.lang);
            if (!label) {
              label = lng;
            }
          }
          if (this.#rmp.customTrackNames.captions.length > 0 && this.#rmp.customTrackNames.captions[index]) {
            label = this.#rmp.customTrackNames.captions[index];
          }
          this.#rmp.loadedCCFiles[index].name = label;
          this.#rmp.loadedCCFiles[index].uri = textTrack.src || '';
          this.#rmp.loadedCCFiles[index].data = null;
          // update UI for HLS DEFAULT=YES attribute
          if (textTrack.mode && textTrack.mode === 'showing') {
            this.#rmp.loadedCCFiles[index].default = true;
          }
        });
        this.#rmp.rmpCCManagement.appendUI();
      }
    }
  }

  #hlsJSAbrInterval() {
    const currentLevel = this.#rmp.hlsJS.currentLevel;
    if (currentLevel > -1 && this.#rmp.dom.qualityOverlayLevelsArea) {
      const qualities = this.#rmp.dom.qualityOverlayLevelsArea.querySelectorAll('.rmp-overlay-level');
      if (qualities.length > 0) {
        for (let i = 0, len = qualities.length; i < len; i++) {
          qualities[i].classList.remove('rmp-abr-active');
        }
        qualities[currentLevel + 1].classList.add('rmp-abr-active');
        this.#rmp.rmpStreamingHelpers.updateQualityModuleIcon(qualities[currentLevel + 1]);
      }
    }
  }

  #getFrameRate() {
    const levels = this.#rmp.hlsJS.levels;
    let currentLevelId = 0;
    if (this.#rmp.hlsJS.currentLevel > -1) {
      currentLevelId = this.#rmp.hlsJS.currentLevel;
    }
    if (typeof levels[currentLevelId].frameRate === 'number' && levels[currentLevelId].frameRate > 0) {
      if (this.#rmp.frameRate !== levels[currentLevelId].frameRate) {
        this.#rmp.frameRate = levels[currentLevelId].frameRate;
        Logger.print('hls.js', 'updated framerate is: ' + this.#rmp.frameRate);
        this.#rmp.dispatch('framerateavailable');
      }
    }
  }

  #attachHlsJSStreams() {
    const levels = this.#rmp.hlsJS.levels;
    if (Array.isArray(levels) && levels.length > 0) {
      Logger.print('hls.js', 'parsed levels follow', levels);
      // reOrder Levels By Height
      levels.sort((a, b) => {
        if (a.height && b.height) {
          return a.height - b.height;
        }
        return 0;
      });

      // framerate handling
      // in case frameRate is set with input setting we already have frameRate
      if (this.#rmp.frameRate === -1) {
        this.#getFrameRate();
        this.#hlsJSFramerateIntervalFn = window.setInterval(() => {
          this.#getFrameRate();
        }, 16.67);
      }

      this.#rmp.rmpStreamingHelpers.setUpAbrUI();
      const trackLabel = this.#rmp.labels.hint.track;
      if (this.#rmp.dom.qualityAuto) {
        const selectRenditionZeroFn = this.#rmp.rmpStreamingHelpers.selectRendition.bind(this.#rmp.rmpStreamingHelpers, 0);
        FW.addEvents(['touchend', 'click'], this.#rmp.dom.qualityAuto, selectRenditionZeroFn);
        levels.forEach((level, index) => {
          const newStream = FW.createElement(
            'button',
            'rmp-button rmp-overlay-level rmp-color-bg rmp-q' + (index + 1),
            null,
            this.#rmp.rtl
          );
          let label = '';
          let accessibleLabel = trackLabel + ' ' + (index + 1);
          if (level) {
            if (typeof level.name === 'string' && level.name !== '') {
              label += level.name;
              accessibleLabel = level.name;
              FW.setText(newStream, label);
            } else if (typeof level.bitrate === 'number' || typeof level.height === 'number') {
              if (typeof level.height === 'number' && level.height > 0) {
                label = 'on';
                accessibleLabel = level.height.toString() + 'p';
                newStream.appendChild(StreamingHelpers.getLevelHeight(level));
              }
              if (typeof level.bitrate === 'number' && level.bitrate > 10000 && (this.#rmp.bitrateDataDisplayed === 'kbps' || this.#rmp.bitrateDataDisplayed === 'mbps')) {
                label = 'on';
                if (typeof level.height === 'number' && level.height > 0) {
                  const span = document.createElement('span');
                  FW.setText(span, ' @ ');
                  newStream.appendChild(span);
                  newStream.appendChild(StreamingHelpers.getBandwidth(level.bitrate, this.#rmp.bitrateDataDisplayed));
                } else {
                  newStream.appendChild(StreamingHelpers.getBandwidth(level.bitrate, this.#rmp.bitrateDataDisplayed));
                }
              }
              if (label === '' && typeof level.bitrate === 'number' && level.bitrate > 10000) {
                label = 'on';
                if (this.#rmp.bitrateDataDisplayed === 'none') {
                  this.#rmp.bitrateDataDisplayed = 'kbps';
                }
                accessibleLabel = Math.round(level.bitrate / 1000);
                newStream.appendChild(StreamingHelpers.getBandwidth(level.bitrate, this.#rmp.bitrateDataDisplayed));
              }
            }
            if (level.videoCodec && this.#rmp.displayCodecData) {
              const span = document.createElement('span');
              StreamingHelpers.getCodecLabel(level, span);
              newStream.appendChild(span);
            }
          }
          if (this.#rmp.customTrackNames.quality.length > 0 && this.#rmp.customTrackNames.quality[index]) {
            accessibleLabel = this.#rmp.customTrackNames.quality[index];
            FW.setText(newStream, this.#rmp.customTrackNames.quality[index]);
          } else if (label === '') {
            FW.setText(newStream, trackLabel + ' ' + (index + 1));
          }
          this.#rmp.rmpAccessible.button(newStream, accessibleLabel, true);
          const selectRenditionFn = this.#rmp.rmpStreamingHelpers.selectRendition.bind(this.#rmp.rmpStreamingHelpers, index + 1);
          FW.addEvents(['touchend', 'click'], newStream, selectRenditionFn);
          this.#rmp.dom.qualityOverlayLevelsArea.appendChild(newStream);
        });
      }
    }
  }

  #hlsManifestParsed() {
    this.#rmp.hlsJS.off(window.Hls.Events.MANIFEST_PARSED, this.#hlsManifestParsedFn);
    this.#attachHlsJSStreams();
    this.#rmp.dispatch('manifestparsed');
  }

  #hlsJSAttachMultiAudio() {
    this.#rmp.hlsJS.off(window.Hls.Events.AUDIO_TRACKS_UPDATED, this.#hlsJSAttachMultiAudioFn);
    Logger.print('hls.js', 'detected audio tracks follow', this.#rmp.hlsJS.audioTracks);
    if (this.#rmp.hlsJS.audioTracks.length > 1) {
      this.#rmp.rmpModuleManagement.appendToControlBar('audio');
      this.#rmp.rmpModuleManagement.appendOverlay('audio');
      let langDiv;
      // re-arrange audio tracks in case default is not at index 0 in manifest
      // first check for default tracks - if more than 1 ignore re-arrange cf Apple sample
      let defaultIndex = this.#rmp.hlsJS.audioTracks.findIndex(value => {
        return value.default;
      });
      if (defaultIndex < 0) {
        defaultIndex = 0;
      }
      this.#rmp.hlsJS.audioTracks.forEach((filteredAudioTrack, index) => {
        if (filteredAudioTrack.type === 'AUDIO') {
          let className;
          if (index === defaultIndex) {
            className = 'rmp-button rmp-overlay-level rmp-color-bg rmp-overlay-level-active';
          } else {
            className = 'rmp-button rmp-overlay-level rmp-color-bg';
          }
          let textContent = '';
          if (this.#rmp.customTrackNames.audio.length > 0 && this.#rmp.customTrackNames.audio[index]) {
            textContent = this.#rmp.customTrackNames.audio[index];
          } else if (typeof filteredAudioTrack.name === 'string' && filteredAudioTrack.name !== '') {
            textContent = filteredAudioTrack.name;
          } else if (typeof filteredAudioTrack.lang === 'string' && filteredAudioTrack.lang !== '') {
            textContent = FW.mapLanguageCodeToName(filteredAudioTrack.lang);
          } else {
            textContent = 'Audio ' + index;
          }
          if (parseInt(filteredAudioTrack.channels) > 2) {
            textContent += ' - surround';
          }
          langDiv = FW.createElement(
            'button',
            className,
            textContent,
            this.#rmp.rtl,
            true
          );
          this.#rmp.rmpAccessible.button(langDiv, textContent, true);
          const selectAudioTrackFn = this.#rmp.rmpStreamingHelpers.selectAudioTrack.bind(this.#rmp.rmpStreamingHelpers, index);
          FW.addEvents(['touchend', 'click'], langDiv, selectAudioTrackFn);
          this.#rmp.dom.audioOverlayLevelsArea.appendChild(langDiv);
        }
      });
    }
  }

  #liveToVOD() {
    this.#rmp.isDvr = false;
    this.#rmp.isVod = true;
    this.#rmp.detectLive = false;
    this.#rmp.firstDurationChange = true;
    this.#rmp.dom.container.classList.remove('rmp-dvr-vod', 'rmp-dvr-live', 'rmp-dvr');
    FW.hide(this.#rmp.dom.dvrIcon);
    if (this.#rmp.rmpDvrManagement) {
      this.#rmp.rmpDvrManagement.destroy();
    }
    FW.setText(this.#rmp.dom.timeElapsedText, '00:00');
    this.#rmp.rmpEventsManagement.durationchangeCallback();
    this.#rmp.rmpUtils.refreshHtml5VideoEndedManagement();
  }

  #onFragParsed() {
    this.#fragParsedNBFragment++;
    if (this.#fragParsedNBFragment === 2) {
      Logger.print('hls.js', 'preloading manifest and first few segments');
      this.#rmp.hlsJS.stopLoad();
    }
  }

  #onLevelLoaded(event, data) {
    if (data && data.details) {
      const levelDetails = this.#hlsLevelData = data.details;
      // live/DVR handling
      if (levelDetails.live && typeof levelDetails.totalduration === 'number' &&
        levelDetails.totalduration > this.#rmp.dvrUIThreshold) {
        if (!this.#rmp.rmpDvrManagement) {
          this.#rmp.rmpDvrManagement = new DvrManagement(this.#rmp);
        }
        this.#rmp.rmpDvrManagement.appendDvrUI();
        this.#hlsJSLevelDuration = levelDetails.totalduration;
      }
      if (this.#rmp.isDvr) {
        if (!levelDetails.live) {
          Logger.print('hls.js', 'DVR UI but stream is no longer a live stream');
          this.#liveToVOD();
        }
      } else if (this.#rmp.detectLive) {
        Logger.print('hls.js', 'stream is a live stream: ' + levelDetails.live);
        if (levelDetails.live) {
          this.#rmp.rmpUIManagement.prepareLiveUI();
        } else {
          this.#rmp.rmpSeekManagement.appendSeekBar();
        }
        this.#rmp.detectLive = false;
      }
      this.#rmp.dispatch('levelloaded');
    }
  }

  #onManifestLoaded(event, data) {
    if (typeof data !== 'undefined') {
      this.#hlsManifestData.levels = [];
      this.#hlsManifestData.audioTracks = [];
      this.#hlsManifestData.subtitles = [];
      this.#hlsManifestData.captions = [];
      if (Array.isArray(data.levels)) {
        this.#hlsManifestData.levels = data.levels;
      }
      if (Array.isArray(data.audioTracks)) {
        this.#hlsManifestData.audioTracks = data.audioTracks;
      }
      if (Array.isArray(data.subtitles)) {
        this.#hlsManifestData.subtitles = data.subtitles;
      }
      if (Array.isArray(data.captions)) {
        this.#hlsManifestData.captions = data.captions;
      }
      Logger.print('hls.js', 'hlsManifestData', this.#hlsManifestData);
      if (typeof data.sessionData === 'object' && data.sessionData !== null) {
        this.#hlsSessionData = data.sessionData;
      }
      Logger.print('hls.js', 'hlsSessionData', this.#hlsSessionData);
    }
    this.#rmp.dispatch('manifestloaded');
  }

  #onFragChanged(event, data) {
    if (data && data.frag) {
      this.#hlsFragmentBeingPlayed = data.frag;
      this.#rmp.dispatch('fragmentbeingplayedchanged');

      if (data.frag.tagList) {
        const tagList = data.frag.tagList;

        const dateRangeValue = tagList.find(element => {
          return element[0] === 'EXT-X-DATERANGE';
        });
        if (dateRangeValue) {
          this.#rmp.meta = {};
          this.#rmp.meta.type = 'date-range';
          this.#rmp.meta.data = {};
          if (typeof data.frag.start === 'number') {
            this.#rmp.meta.data.start = data.frag.start;
            if (typeof data.frag.duration === 'number') {
              this.#rmp.meta.data.end = data.frag.duration + data.frag.start;
              this.#rmp.meta.data.duration = data.frag.duration;
            }
          }
          this.#rmp.meta.data.metadata = {};
          this.#rmp.meta.data.metadata.tag = 'EXT-X-DATERANGE';
          this.#rmp.meta.data.metadata.content = dateRangeValue[1];
          const attributes = dateRangeValue[1].split(',');
          this.#rmp.meta.data.metadata.attributes = [];
          if (attributes.length > 0) {
            attributes.forEach(attribute => {
              const cleanedAttribute = attribute.replaceAll('"', '');
              const attributeData = cleanedAttribute.split('=');
              if (attributeData.length > 0) {
                this.#rmp.meta.data.metadata.attributes.push(
                  {
                    name: attributeData[0],
                    value: attributeData[1]
                  }
                );
              }
            });
          }
          this.#rmp.dispatch('meta');
          Logger.print(null, null, JSON.parse(JSON.stringify(this.#rmp.meta)));
        }

        const programDateTime = tagList.find(element => {
          return element[0] === 'PROGRAM-DATE-TIME';
        });
        if (programDateTime && data.frag.programDateTime) {
          this.#rmp.meta = {};
          this.#rmp.meta.type = 'program-date-time';
          this.#rmp.meta.data = {};
          this.#rmp.meta.data.programDateTime = data.frag.programDateTime;
          if (typeof data.frag.endProgramDateTime === 'number') {
            this.#rmp.meta.data.endProgramDateTime = data.frag.endProgramDateTime;
          }
          if (typeof data.frag.rawProgramDateTime === 'string') {
            this.#rmp.meta.data.rawProgramDateTime = data.frag.rawProgramDateTime;
            this.#rawProgramDateTime = data.frag.rawProgramDateTime;
          }
          if (typeof data.frag.start === 'number') {
            this.#rmp.meta.data.start = data.frag.start;
            if (typeof data.frag.duration === 'number') {
              this.#rmp.meta.data.end = data.frag.duration + data.frag.start;
            }
          }
          this.#rmp.dispatch('meta');
          Logger.print(null, null, JSON.parse(JSON.stringify(this.#rmp.meta)));
        }

        const cueInValue = tagList.find(element => {
          return element[0] === 'EXT-X-CUE-IN';
        });
        const cueOutValue = tagList.find(element => {
          return element[0] === 'EXT-X-CUE-OUT';
        });
        if (cueInValue || cueOutValue) {
          this.#rmp.meta = {};
          this.#rmp.meta.type = 'scte-35';
          this.#rmp.meta.data = {};
          if (typeof data.frag.start === 'number') {
            this.#rmp.meta.data.start = data.frag.start;
            if (typeof data.frag.duration === 'number') {
              this.#rmp.meta.data.end = data.frag.duration + data.frag.start;
              this.#rmp.meta.data.duration = data.frag.duration;
            }
          }
          this.#rmp.meta.data.metadata = {};
          let tag, content = '';
          if (cueOutValue) {
            if (cueOutValue[1]) {
              content = cueOutValue[1];
            }
            tag = 'EXT-X-CUE-OUT';
          } else {
            if (cueInValue[1]) {
              content = cueInValue[1];
            }
            tag = 'EXT-X-CUE-IN';
          }
          this.#rmp.meta.data.metadata.tag = tag;
          this.#rmp.meta.data.metadata.content = content;
          this.#rmp.dispatch('meta');
          Logger.print(null, null, JSON.parse(JSON.stringify(this.#rmp.meta)));
        }

      }
    }
  }

  #onFragParsingUserdata(event, data) {
    if (data && typeof data.samples !== 'undefined' && data.samples[0]) {
      this.#rmp.meta = {};
      this.#rmp.meta.type = 'sei';
      this.#rmp.meta.data = {};
      this.#rmp.meta.data.samples = data.samples;
      /*data:
  samples: Array(1)
  0:
  pts: 0.03333333333333333
  payloadType: 5
  uuid: "8fbb6c74-7c3e-4f78-9f7-8cb35d3c177e"
  userDataBytes: {0: 69, 1: 108, 2: 101, 3: 109, 4: 101, 5: 110, 6: 116, 7: 97, 8: 108, 9: 32, 10: 86, 11: 105, 12: 100, 13: 101, 14: 111, 15: 32, 16: 69, 17: 110, 18: 103, 19: 105, 20: 110, 21: 101, 22: 40, 23: 116, 24: 109, 25: 41, 26: 32, 27: 119, 28: 119, 29: 119, 30: 46, 31: 101, 32: 108, 33: 101, 34: 109, 35: 101, 36: 110, 37: 116, 38: 97, 39: 108, 40: 116, 41: 101, 42: 99, 43: 104, 44: 110, 45: 111, 46: 108, 47: 111, 48: 103, 49: 105, 50: 101, 51: 115, 52: 46, 53: 99, 54: 111, 55: 109}
  userData: "Elemental Video Engine(tm) www.elementaltechnologies.com"*/
      this.#rmp.dispatch('meta');
      Logger.print(null, null, JSON.parse(JSON.stringify(this.#rmp.meta)));
    }
  }

  #hlsJSEventHandling() {
    const Events = window.Hls.Events;
    // MANIFEST_PARSED 
    this.#hlsManifestParsedFn = this.#hlsManifestParsed.bind(this);
    this.#rmp.hlsJS.on(Events.MANIFEST_PARSED, this.#hlsManifestParsedFn);

    // on playing handle hlsJSStopDownloadWhilePaused
    if (this.#rmp.hlsJSStopDownloadWhilePaused) {
      this.#rmp.dom.video.addEventListener('playing', () => {
        this.#rmp.dom.video.addEventListener('pause', () => {
          this.#rmp.hlsJS.stopLoad();
        });
        this.#rmp.dom.video.addEventListener('play', () => {
          this.#rmp.hlsJS.startLoad();
        });
      }, { once: true });
    }

    // FRAG_LOADED
    this.#rmp.hlsJS.on(Events.FRAG_LOADED, (event, data) => {
      if (data && data.frag) {
        this.#hlsFragmentData = data.frag;
        this.#rmp.dispatch('fragmentloaded');
      }
    });

    // FRAG_PARSED 
    if (this.#rmp.preload === 'metadata') {
      this.#rmp.hlsJSPreloadManager = true;
      this.#onFragParsedFn = this.#onFragParsed.bind(this);
      this.#rmp.hlsJS.on(Events.FRAG_PARSED, this.#onFragParsedFn);
    }

    // ABR level
    this.#hlsJSAbrInterval();
    window.clearInterval(this.#hlsJSAbrIntervalFn);
    this.#hlsJSAbrIntervalFn = window.setInterval(this.#hlsJSAbrInterval.bind(this), 400);

    // LEVEL_SWITCHING
    this.#rmp.hlsJS.on(Events.LEVEL_SWITCHING, () => {
      this.#rmp.dispatch('qualitychanging');
    });
    this.#rmp.hlsJS.on(Events.LEVEL_SWITCHED, () => {
      this.#rmp.dispatch('qualitychanged');
    });

    // LEVEL_LOADED
    this.#rmp.hlsJS.on(Events.LEVEL_LOADED, this.#onLevelLoaded.bind(this));

    // MANIFEST_LOADED
    this.#rmp.hlsJS.on(Events.MANIFEST_LOADED, this.#onManifestLoaded.bind(this));

    // FRAG_CHANGED
    this.#rmp.hlsJS.on(Events.FRAG_CHANGED, this.#onFragChanged.bind(this));

    // INTERSTITIALS_UPDATED 
    // INTERSTITIAL_STARTED INTERSTITIAL_ENDED 
    // INTERSTITIAL_ASSET_STARTED INTERSTITIAL_ASSET_ENDED 
    // INTERSTITIAL_ASSET_ERROR 

    this.#rmp.hlsJS.on(Events.INTERSTITIALS_UPDATED, (event, data) => {
      this.#rmp.dispatch('interstitialsupdated', data);
    });

    this.#rmp.hlsJS.on(Events.INTERSTITIAL_STARTED, () => {
      if (this.#rmp.interstitialShowAdUI) {
        if (this.#rmp.adCountDown) {
          if (!this.#rmp.dom.adInfo) {
            this.#rmp.rmpAdsHelpers.createControlBarAdInfo();
            this.#rmp.rmpAdsHelpers.addCountDown();
          }
        }
        if (this.#rmp.adShowRemainingTime) {
          this.#rmp.rmpAdsHelpers.appendRemainingTimeBar();
        }
        this.#rmp.adUI = true;
      }
      this.#rmp.dispatch('interstitialstarted');
    });

    this.#rmp.hlsJS.on(Events.INTERSTITIAL_ENDED, () => {

      if (this.#rmp.interstitialShowAdUI) {
        this.#rmp.adUI = false;
      }
      this.#rmp.dispatch('interstitialended');
    });

    this.#rmp.hlsJS.on(Events.INTERSTITIAL_ASSET_STARTED, (event, data) => {
      this.#rmp.dispatch('interstitialassetstarted', data);
    });

    this.#rmp.hlsJS.on(Events.INTERSTITIAL_ASSET_ENDED, (event, data) => {
      this.#rmp.dispatch('interstitialassetended', data);
    });

    this.#rmp.hlsJS.on(Events.INTERSTITIAL_ASSET_ERROR, (event, data) => {
      this.#rmp.dispatch('interstitialasseterror', data);
      this.#rmp.rmpUtils.warning(2001);
    });

    if (!this.#rmp.audioOnly) {
      // TEXT_TRACK 
      if (this.#hlsJSConfig.enableCaptions) {
        this.#rmp.one('loadeddata', () => {
          if (this.#rmp.hlsJS.subtitleTracks.length > 0) {
            Logger.print('captions', 'hls.js subtitle tracks detected', this.#rmp.hlsJS.subtitleTracks);
            this.#hlsJSTextTracksCheckingInterval();
          } else {
            this.#hlsJSTextTracksCheckingIntervalFn = window.setInterval(
              this.#hlsJSTextTracksCheckingInterval.bind(this),
              400);
          }
        });
        if (this.#rmp.autoBumpCaptionsOnVisibleControlBar) {
          UIManagement.appendCSSForNativeCCRendering();
        }
      }

      // AUDIO_TRACK_SWITCHING
      this.#rmp.hlsJS.on(Events.AUDIO_TRACK_SWITCHING, () => {
        this.#rmp.dispatch('audiotrackchanging');
      });
      this.#rmp.hlsJS.on(Events.AUDIO_TRACK_SWITCHED, () => {
        this.#rmp.dispatch('audiotrackchanged');
      });
      this.#hlsJSAttachMultiAudioFn = this.#hlsJSAttachMultiAudio.bind(this);
      this.#rmp.hlsJS.on(Events.AUDIO_TRACKS_UPDATED, this.#hlsJSAttachMultiAudioFn);
    }

    // FRAG_PARSING_USERDATA - SEI packets with resolution payloadType 5
    this.#rmp.hlsJS.on(Events.FRAG_PARSING_USERDATA, this.#onFragParsingUserdata.bind(this));

    //fired when parsing ID3 is completed
    //data: { id: demuxer id, frag : fragment object, samples : [ ID3 pes - pts and dts timestamp are relative, values are in seconds], details: LevelDetails }
    this.#rmp.hlsJS.on(Events.FRAG_PARSING_METADATA, (event, data) => {
      if (data && data.samples && data.samples.length > 0) {
        this.#hlsRawID3KLVSamplesData = data.samples;
        this.#rmp.dispatch('hlsid3klvparsed');
        Logger.print('hls.js', 'ID3/KLV metadata parsed', this.#hlsRawID3KLVSamplesData);
      }
    });
  }

  #onPlayingCurrentLevelResetTried() {
    this.#rmp.off('playing', this.#onPlayingCurrentLevelResetTriedFn);
    this.#currentLevelResetTried = false;
  }

  #createHlsJSInstance() {
    this.#hlsJSConfig = new ConfigureHlsJS(this.#rmp);
    if (!FW.isEmptyObject(this.#rmp.hlsJSFullCustomConfig)) {
      this.#hlsJSValidatedConfig = this.#rmp.hlsJSFullCustomConfig;
    } else {
      this.#hlsJSValidatedConfig = this.#hlsJSConfig.prepareConfig();
    }
    Logger.print('hls.js', 'hls.js config follows', this.#hlsJSValidatedConfig);
    if (this.#rmp.p2pMediaLoaderEngine !== null) {
      Logger.print('hls.js', 'p2pMediaLoader enabled', this.#rmp.p2pMediaLoaderEngine);
      const HlsWithP2P = this.#rmp.p2pMediaLoaderEngine.injectMixin(window.Hls);
      this.#hlsJSValidatedConfig.p2p = this.#rmp.p2pMediaLoaderConfig;
      this.#rmp.hlsJS = new HlsWithP2P(this.#hlsJSValidatedConfig);
    } else {
      this.#rmp.hlsJS = new window.Hls(this.#hlsJSValidatedConfig);
    }


    // Bitmovin Analytics
    if (window.bitmovin && this.#rmp.bitmovinAnalyticsConfig.key && typeof this.#rmp.bitmovinAnalyticsConfig.key === 'string') {
      Logger.print('bitmovin analytics', 'adding hls.js analytics support', this.#rmp.bitmovinAnalyticsConfig);
      this.#rmp.bitmovinAnalytics = new window.bitmovin.analytics.adapters.HlsAdapter(
        this.#rmp.bitmovinAnalyticsConfig,
        this.#rmp.hlsJS
      );
      Logger.print('bitmovin analytics', 'bitmovinAnalytics', this.#rmp.bitmovinAnalytics);
    }

    this.#hlsJSEventHandling();

    if (this.#rmp.adImaDai) {
      this.#rmp.rmpAdsImaDai = new AdsImaDai(this.#rmp);
      this.#rmp.rmpAdsImaDai.init();
      return;
    }

    // error
    let recoverDecodingErrorDate = 0;
    let recoverSwapAudioCodecDate = 0;
    const levelOrFragErrors = [
      window.Hls.ErrorDetails.LEVEL_LOAD_ERROR,
      window.Hls.ErrorDetails.LEVEL_LOAD_TIMEOUT,
      window.Hls.ErrorDetails.FRAG_LOAD_ERROR,
      window.Hls.ErrorDetails.FRAG_LOAD_TIMEOUT
    ];
    this.#onPlayingCurrentLevelResetTriedFn = this.#onPlayingCurrentLevelResetTried.bind(this);
    this.#rmp.hlsJS.on(window.Hls.Events.ERROR, (event, data) => {
      if (data && typeof data.fatal === 'boolean' && data.details && data.type) {
        if (data.fatal) {
          if (data.type === window.Hls.ErrorTypes.NETWORK_ERROR) {
            Logger.print('hls.js', 'fatal network error encountered, try to recover');
            const levelOrFragError = levelOrFragErrors.indexOf(data.details) > -1;
            if (levelOrFragError && !this.#rmp.innerAbrAutoMode) {
              Logger.print('hls.js', data.details + ' - error for manual switching -> trying to restore ABR mode');
              this.#rmp.quality = -1;
              this.#rmp.hlsJS.startLoad();
              return;
            } else if (data.details === window.Hls.ErrorDetails.MANIFEST_PARSING_ERROR) {
              this.#rmp.rmpUtils.error(203, data);
              return;
            } else {
              Logger.print('hls.js', 'fatal network error - no recovery option');
              this.#rmp.rmpUtils.error(204, data);
              return;
            }
          } else if (data.type === window.Hls.ErrorTypes.MEDIA_ERROR) {
            Logger.print('hls.js', 'fatal media error encountered, try to recover');
            // incompatible codecs (example HEVC in FF) - no need to try recoverMediaError
            if (data.details === window.Hls.ErrorDetails.MANIFEST_INCOMPATIBLE_CODECS_ERROR) {
              this.#rmp.rmpUtils.error(205, data);
              return;
            }
            const now = FW.getNow();
            if (recoverDecodingErrorDate === 0 || (now - recoverDecodingErrorDate) > 3000) {
              recoverDecodingErrorDate = FW.getNow();
              // recover in case capLevelToPlayerSize causes issue
              if (this.#rmp.hlsJS.config.capLevelToPlayerSize) {
                Logger.print('hls.js', 'try to recover from capLevelToPlayerSize failure');
                this.#rmp.hlsJS.config.capLevelToPlayerSize = false;
                recoverDecodingErrorDate = 0;
                this.#rmp.hlsJS.autoLevelCapping = -1;
                this.#rmp.hlsJS.recoverMediaError();
              } else if (!this.#currentLevelResetTried) {
                Logger.print('hls.js', 'try to recover media failure with currentLevel reset');
                this.#currentLevelResetTried = true;
                this.#rmp.on('playing', this.#onPlayingCurrentLevelResetTriedFn);
                recoverDecodingErrorDate = 0;
                this.#rmp.hlsJS.currentLevel = 0;
                this.#rmp.hlsJS.recoverMediaError();
              } else {
                Logger.print('hls.js', 'try to recover media failure recoverMediaError');
                this.#rmp.off('playing', this.#onPlayingCurrentLevelResetTriedFn);
                this.#rmp.hlsJS.recoverMediaError();
              }
            } else {
              if (recoverSwapAudioCodecDate === 0 || (now - recoverSwapAudioCodecDate) > 3000) {
                recoverSwapAudioCodecDate = FW.getNow();
                Logger.print('hls.js', 'calling swapAudioCodec then recoverMediaError now');
                this.#rmp.hlsJS.swapAudioCodec();
                this.#rmp.hlsJS.recoverMediaError();
              } else {
                this.#rmp.rmpUtils.error(206, data);
                return;
              }
            }
          } else {
            this.#rmp.rmpUtils.error(201, data);
            return;
          }
        } else {
          this.#rmp.rmpUtils.warning(2000, data);
        }
      }
    });
    this.initFinised();
  }

  #loadHlsJS() {
    if (typeof window.Hls !== 'undefined') {
      this.#createHlsJSInstance();
    } else {
      // do not change to const pathToRmpFiles = this.#rmp.pathToRmpFiles or grunt replace won't work
      let callScript = this.#rmp.pathToRmpFiles + 'hls/hls.min.js';
      if (DEBUG) {
        callScript = this.#rmp.pathToRmpFiles + 'hls/hls.js';
      }
      Logger.print('hls.js', 'hls.js starts loading at ' + (FW.getNow() - this.#rmp.refTime) + ' ms');
      FW.getScript(
        callScript,
        this.#createHlsJSInstance.bind(this),
        this.#rmp.rmpUtils.error.bind(this.#rmp.rmpUtils, 200),
        this.#rmp.shadowRoot
      );
    }
  }

  unwireFragFirstCheck() {
    this.#rmp.hlsJS.off(window.Hls.Events.FRAG_PARSED, this.#onFragParsedFn);
  }

  syncHlsJSToLiveEdge() {
    if (typeof this.#rmp.hlsJS.liveSyncPosition === 'number' && this.#rmp.hlsJS.liveSyncPosition > 0) {
      Logger.print('hls.js', 'jumping to live edge at ' + this.#rmp.hlsJS.liveSyncPosition);
      this.#rmp.dom.video.currentTime = this.#rmp.hlsJS.liveSyncPosition;
    }
  }

  destroy() {
    if (this.#rmp.hlsJS) {
      this.#rmp.hlsJS.destroy();
      this.#rmp.hlsJS.bufferTimer = undefined;
      window.clearInterval(this.#rmp.hlsJS.bufferTimer);
    }
    this.#hlsJSLevelDuration = -1;
    this.#rmp.off('playing', this.#onPlayingCurrentLevelResetTriedFn);
    window.clearInterval(this.#hlsJSFramerateIntervalFn);
    window.clearInterval(this.#hlsJSAbrIntervalFn);
    window.clearInterval(this.#hlsJSTextTracksCheckingIntervalFn);
    this.#resetHlsJSVars();
  }

  init() {
    if (this.#rmp.msePreloadRequested) {
      this.#rmp.msePreloadRequested = false;
      return;
    }
    this.#loadHlsJS();
  }

  initFinised() {
    this.#rmp.hlsJS.attachMedia(this.#rmp.dom.video);
    Logger.print('hls.js', 'loading source manifest now at ' + this.#rmp.startingSrcUri);
    this.#rmp.hlsJS.loadSource(this.#rmp.startingSrcUri);
    // hls.js MUX 
    if (this.#rmp.rmpMux) {
      this.#rmp.rmpMux.initHlsJS();
    }
    this.#rmp.hlsjsInstanceCreated = true;
    this.#rmp.dispatch('hlsjsinstancecreated');
  }

}
